/**
 * Everything a recovery case needs, on the screen where the group was found.
 *
 * The bundle export lived inside a collapsed procedure card with a group-ID
 * field of its own, which meant looking a group up and then typing its id in
 * again somewhere else. Worse, it answered "how do I get a bundle" with "expand
 * the third accordion", and the export people found first was the all-groups
 * one — an array, which the tool then rejected as "not a recovery bundle".
 *
 * So: who is in the group, who left and when, whether each person can be sealed
 * to, whether the group is escrowed at all, and the download — together,
 * because they are read together.
 *
 * “Who left and when” is here for a specific case: an account is taken over and
 * the intruder removes its owner from a group. The date is the evidence, and
 * the row carries the undo — which needs the recovery shares, because it hands
 * out a *role*, and a role is control rather than reading. An owner can remove
 * every member and delete the group, none of which needs a key that works.
 */

import { useQuery } from "@tanstack/react-query";
import { errorMessage, trpc } from "../../lib/trpc.js";
import { Badge } from "../../components/ui/Badge.js";
import { Skeleton } from "../../components/ui/Skeleton.js";
import { ExportGroupBundle } from "./ExportBundles.js";
import { ReadmitMember } from "./ReadmitMember.js";
import "./GroupRecovery.css";

export function GroupRecovery({ groupId }: { groupId: string }) {
  const detail = useQuery(trpc.admin.groupMembers.queryOptions({ groupId }));

  if (detail.isLoading) return <Skeleton height="160px" radius="md" />;
  if (detail.isError) {
    return (
      <p className="fc-admin__error" role="alert">
        {errorMessage(detail.error)}
      </p>
    );
  }
  if (!detail.data) return null;

  const { members, current, past, escrow } = detail.data;

  return (
    <section className="fc-grecovery">
      <div className="fc-grecovery__head">
        <h4>Recovery</h4>
        <ExportGroupBundle groupId={groupId} />
      </div>

      {/*
        Said before the download button is pressed rather than after. An
        unescrowed group cannot be exported at all, and discovering that from a
        failed download reads as a broken button.
      */}
      {escrow ? (
        <p className="fc-admin__hint">
          Sealed to recovery key <code>{escrow.recoveryKeyId}</code>
          {escrow.label ? ` (${escrow.label})` : ""}
          {escrow.retiredAt ? (
            <>
              {" — "}
              <strong>retired.</strong> Its shares are a different set from the
              current key&rsquo;s.
            </>
          ) : (
            " — the current key."
          )}
        </p>
      ) : (
        <p className="fc-admin__error" role="status">
          This group has never been sealed to a recovery key, so nothing here
          can open it. A member has to protect it from their account screen
          first.
        </p>
      )}

      <p className="fc-admin__hint">
        {current} {current === 1 ? "member" : "members"}
        {past > 0 && `, ${String(past)} who left`}
      </p>

      <ul className="fc-grecovery__members" role="list">
        {members.map((member) => (
          <li key={member.userId} data-left={member.leftAt ? "" : undefined}>
            <span className="fc-grecovery__who">
              <strong>{member.displayName}</strong>
              <code>{member.username}</code>
              {member.email && (
                <span className="fc-grecovery__email">{member.email}</span>
              )}
            </span>

            <span className="fc-grecovery__tags">
              <Badge variant="neutral">{member.role}</Badge>

              {member.leftAt ? (
                <>
                  <Badge variant="danger">
                    left {new Date(member.leftAt).toLocaleDateString()}
                  </Badge>
                  {/* The date is the evidence in a takeover; this is the undo. */}
                  <ReadmitMember
                    groupId={groupId}
                    userId={member.userId}
                    name={member.displayName}
                    previousRole={member.role}
                  />
                </>
              ) : (
                /*
                 * A current member with no key is the ordinary "invited but
                 * has not opened it yet" state — and also what somebody looks
                 * like after their keyring was lost, which is the case that
                 * brings people to this screen.
                 */
                !member.hasKey && <Badge variant="danger">no key</Badge>
              )}

              {!member.hasPublicKey && (
                <Badge variant="danger">no public key</Badge>
              )}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
